/**
 * A menu of actions or options, opened from a button (see /styleguide): in
 * the iOS app, iOS's own; elsewhere in a popover (ui/Popover). Its
 * sections are split by a line, each maybe titled, and a tick marks an
 * option picked.
 */
import * as preact from "preact";

import * as iOSBridge from "iOSBridge";
import { NativeActions, type NativeMenuItemSpec } from "nativeMenuItems";
import { cn } from "shared/utils";
import Popover from "ui/Popover";
import Check from "views/Check";

export interface MenuItem {
  label: string;
  onSelect?: () => void;
  /** An option: picked, or not (undefined for an action) */
  checked?: boolean;
  disabled?: boolean;
  /** Chosen while disabled, to explain why (on the web; iOS's can't be) */
  onDisabledSelect?: () => void;
  /** Deletes or removes something */
  destructive?: boolean;
  /** A sort's direction, shown beside it */
  direction?: "ascending" | "descending";
  /** Beside its label: an icon font class on the web, an SF Symbol in iOS's */
  icon?: string;
  image?: string;
  /** Chosen, the menu stays open (a toggle among others) */
  keepOpen?: boolean;
  /** ‹ value ›, stepped through in place: a menu with one is the web's */
  stepper?: {
    value: string;
    onPrevious: () => void;
    onNext: () => void;
    previousDisabled?: boolean;
    nextDisabled?: boolean;
  };
}

export interface MenuSection {
  title?: string;
  items: MenuItem[];
}

export interface MenuButtonProps {
  onClick: (event: MouseEvent) => void;
  "aria-expanded": boolean;
  "aria-haspopup": "menu";
}

interface Props {
  sections: MenuSection[];
  /** The button opening it, with these props */
  renderButton: (buttonProps: MenuButtonProps) => preact.ComponentChild;
  /** Which of its button's edges it lines up with (on the web) */
  align?: "start" | "end";
  onOpen?: () => void;
  class?: string;
}

interface State {
  open: boolean;
}

/** The menu as the iOS app draws it: each section inline, under its title */
const nativeItems = (sections: MenuSection[]): NativeMenuItemSpec[] =>
  sections.map((section) => ({
    title: section.title ?? "",
    inline: true,
    children: section.items.map((item) => ({
      title: item.label,
      checked: item.checked,
      disabled: item.disabled,
      destructive: item.destructive,
      image:
        item.image ??
        (item.direction &&
          (item.direction === "ascending" ? "arrow.up" : "arrow.down")),
      perform: item.onSelect,
    })),
  }));

export default class Menu extends preact.Component<Props, State> {
  state: State = { open: false };

  private get sections() {
    return this.props.sections.filter((section) => section.items.length > 0);
  }

  /** In the iOS app, iOS's menu (unless it has a stepper, which iOS's lack) */
  private get showNativeMenu() {
    return this.sections.some((section) =>
      section.items.some((item) => item.stepper)
    )
      ? undefined
      : iOSBridge.get()?.showMenu;
  }

  private close = () => this.setState({ open: false });

  private toggle = async (event: MouseEvent) => {
    event.stopPropagation();
    const showMenu = this.showNativeMenu;
    if (showMenu) {
      this.props.onOpen?.();
      const { x, y, width, height } = (
        event.currentTarget as Element
      ).getBoundingClientRect();
      const actions = new NativeActions();
      const action = await showMenu({
        frame: { x, y, width, height },
        items: actions.describe(nativeItems(this.sections), "menu"),
      });
      actions.perform(action);
      return;
    }
    if (!this.state.open) {
      this.props.onOpen?.();
    }
    this.setState({ open: !this.state.open });
  };

  private choose(item: MenuItem) {
    if (!item.keepOpen) {
      this.close();
    }
    if (item.disabled) {
      item.onDisabledSelect?.();
    } else {
      item.onSelect?.();
    }
  }

  private renderItem(item: MenuItem) {
    const { stepper } = item;
    if (stepper) {
      return (
        <div class="menuItem stepper" key={item.label}>
          <span class="menuCheck"></span>
          <span class="menuLabel">{item.label}</span>
          <span class="menuTrailing">
            <button
              type="button"
              aria-label={`Previous ${item.label}`}
              disabled={stepper.previousDisabled}
              onClick={stepper.onPrevious}
            >
              ‹
            </button>
            <span class="stepperValue">{stepper.value}</span>
            <button
              type="button"
              aria-label={`Next ${item.label}`}
              disabled={stepper.nextDisabled}
              onClick={stepper.onNext}
            >
              ›
            </button>
          </span>
        </div>
      );
    }
    return (
      <button
        type="button"
        key={item.label}
        class={cn({
          menuItem: true,
          destructive: item.destructive,
          disabled: item.disabled,
        })}
        role={item.checked === undefined ? "menuitem" : "menuitemcheckbox"}
        aria-checked={item.checked}
        aria-disabled={item.disabled}
        disabled={item.disabled && !item.onDisabledSelect}
        onClick={() => this.choose(item)}
      >
        <span class="menuCheck">{item.checked ? <Check /> : null}</span>
        <span class="menuLabel">{item.label}</span>
        {item.direction ? (
          <i
            class={`menuTrailing icon-long-arrow-${
              item.direction === "ascending" ? "up" : "down"
            }`}
            aria-label={item.direction}
          ></i>
        ) : item.icon ? (
          <i class={`menuTrailing ${item.icon}`} aria-hidden="true"></i>
        ) : null}
      </button>
    );
  }

  render() {
    return (
      <Popover
        class={cn({
          menu: true,
          [this.props.class ?? ""]: Boolean(this.props.class),
        })}
        popoverClass="menuPopover"
        role="menu"
        align={this.props.align}
        open={this.state.open}
        onClose={this.close}
        anchor={this.props.renderButton({
          onClick: this.toggle,
          "aria-expanded": this.state.open,
          "aria-haspopup": "menu",
        })}
      >
        {this.sections.map((section, index) => (
          <div
            class="menuSection"
            role="group"
            aria-label={section.title}
            key={section.title ?? index}
          >
            {section.title ? (
              <div class="menuSectionTitle">{section.title}</div>
            ) : null}
            {section.items.map((item) => this.renderItem(item))}
          </div>
        ))}
      </Popover>
    );
  }
}
